import React from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useProjectsAnalyticsApprovalStatusReportQuery as useApprovalStatusReportQuery } from 'components/hooks/projectsAnalyticsApprovalStatusReportQueries';
import { useInfiniteProjectsAnalyticsProjectsReportsApprovalStatusReportsDashboardReportQuery as useInfiniteApprovalStatusReportsDashboardReportQuery } from 'components/hooks/projectsAnalyticsProjectsReportsApprovalStatusReportsDashboardReportQueries';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsAnalyticsDashboardContext } from 'components/contexts/ProjectsAnalyticsDashboardContext';
import Card from 'components/projectsAnalytics/components/Card';
import NoDataMessage from 'components/projectsAnalytics/components/NoDataMessage';
import ProjectsModal from 'components/projectsAnalytics/components/ProjectsModal';
import ReportHeader from 'components/projectsAnalytics/components/ReportHeader';
import StatusReportBody from 'components/projectsAnalytics/components/StatusReportBody';
import {
  filtersFormatter,
  filtersListFormatter,
} from 'components/projectsAnalytics/helpers/reportHeader';
import useFilters from 'components/projectsAnalytics/hooks/useFilters';
import useProjectsModal from 'components/projectsAnalytics/hooks/useProjectsModal';

import { pluralizeProjectsName } from 'components/helpers/resources/projects';

const staticChartData = [
  {
    cellClassName: 'tw-fill-green-400',
    countKey: 'approvedCount',
    dotClassName: 'tw-bg-green-400',
    filterName: 'approved',
    tKey: 'approved',
  },
  {
    cellClassName: 'tw-fill-amber-300',
    countKey: 'pendingCount',
    dotClassName: 'tw-bg-amber-300',
    filterName: 'pending_approval',
    tKey: 'pendingApproval',
  },
  {
    cellClassName: 'tw-fill-red-400',
    countKey: 'rejectedCount',
    dotClassName: 'tw-bg-red-400',
    filterName: 'rejected',
    tKey: 'rejected',
  },
  {
    cellClassName: 'tw-fill-mutedBlue-300',
    countKey: 'notSubmittedCount',
    dotClassName: 'tw-bg-mutedBlue-300',
    filterName: 'not_submitted',
    tKey: 'notSubmitted',
  },
];

export default function ApprovalStatusCard({
  dashboardFilterParams,
  isGeneratingExport,
  onExportClick,
}) {
  const currentActor = useCurrentActor();

  const { filters: cardFilters, handleFilterChange: handleCardFilterChange } =
    useFilters({ live: true, future: true });

  const projectsAnalyticsDashboardContext =
    useProjectsAnalyticsDashboardContext();

  const {
    domainDashboardConfigurableFields,
    isApprovalsEnabled,
    isDigitalSignaturesEnabled,
    isFoldersEnabled,
    isProjectReviewDatesEnabled,
    isRiskRegisterEnabled,
  } = projectsAnalyticsDashboardContext;

  const { closeProjectsModal, openProjectsModal, projectsModal } =
    useProjectsModal({
      domainDashboardConfigurableFields,
      isApprovalsEnabled,
      isDigitalSignaturesEnabled,
      isFoldersEnabled,
      isProjectReviewDatesEnabled,
      isRiskRegisterEnabled,
    });

  const { t: i18nTCommon } = useTranslation('common');

  const { data: approvalStatusReport } = useApprovalStatusReportQuery({
    params: { filter: { ...cardFilters, ...dashboardFilterParams } },
    queryOptions: { keepPreviousData: true },
  });

  const {
    data: domainDashboardReport,
    fetchNextPage: fetchDomainDashboardReportNextPage,
    hasNextPage: hasDomainDashboardReportNextPage,
  } = useInfiniteApprovalStatusReportsDashboardReportQuery({
    params: {
      filter: projectsModal.filters,
      selections: projectsModal.selections,
    },
    queryOptions: { enabled: projectsModal.isOpen },
  });

  const handleExportClick = () => {
    onExportClick({ params: { filter: projectsModal.filters } });
  };

  const handleSegmentClick = ({ entry }) => {
    openProjectsModal({
      filters: {
        approval_status: { eq: entry.filterName },
        lifecycle_status: {
          in: _([])
            .tap((lifecycleStatuses) => {
              if (cardFilters.live) lifecycleStatuses.push('live');
              if (cardFilters.future) lifecycleStatuses.push('future');
            })
            .value(),
        },
        ...(dashboardFilterParams.division_ids && {
          company_id: { in: dashboardFilterParams.division_ids },
        }),
      },
      subtitle: pluralizeProjectsName({
        count: entry.count,
        currentActor,
        shouldIncludeCount: true,
      }),
      title: `${i18nTCommon('projects.approvalStatus')}: ${entry.name}`,
    });

    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: segment click',
      {
        chart: 'Approval status',
        filters: cardFilters,
        segment: entry.name,
      },
    );
  };

  const handleProjectClick = (project) => {
    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: project click',
      {
        chart: 'Approval status',
        filters: cardFilters,
        project: project.attributes.name,
      },
    );
  };

  const {
    approvedCount,
    approvedWithConditionsCount,
    futureFilterCount,
    liveFilterCount,
    notSubmittedCount,
    pendingCount,
    rejectedCount,
  } = approvalStatusReport?.data.attributes || {};

  const totalCount = Object.values({
    approvedCount,
    approvedWithConditionsCount,
    pendingCount,
    rejectedCount,
    notSubmittedCount,
  }).reduce((a, b) => a + b, 0);

  const filterCounts = { live: liveFilterCount, future: futureFilterCount };
  const filterDots = { live: 'green', future: 'amber' };

  const dynamicChartData = [
    approvedCount + approvedWithConditionsCount,
    pendingCount,
    rejectedCount,
    notSubmittedCount,
  ];

  const chartData = staticChartData.map((data, index) => ({
    ...data,
    count: dynamicChartData[index],
    name: i18nTCommon(`shared.${data.tKey}`),
  }));

  const filterTranslations = {
    future: i18nTCommon('shared.future'),
    live: i18nTCommon('shared.live'),
  };

  return (
    <Card>
      <ReportHeader
        filters={filtersFormatter({
          filterCounts,
          filterDots,
          filters: cardFilters,
          translations: filterTranslations,
        })}
        minChecked={1}
        onFilterChange={handleCardFilterChange}
        subtitle={filtersListFormatter({
          filters: cardFilters,
          pluralRAMSName: currentActor.division.attributes.pluralRAMSName,
          translations: filterTranslations,
        })}
        title={i18nTCommon('projects.approvalStatus')}
      />
      {approvalStatusReport &&
        (totalCount > 0 ?
          <StatusReportBody
            chartData={chartData}
            onSegmentClick={handleSegmentClick}
            totalCount={totalCount}
          />
        : <NoDataMessage
            message={i18nTCommon('shared.checkAppliedFilters')}
          />)}
      {projectsModal.isOpen && (
        <ProjectsModal
          domainDashboardReport={domainDashboardReport}
          hasNextPage={hasDomainDashboardReportNextPage}
          isGeneratingExport={isGeneratingExport}
          onClose={closeProjectsModal}
          onExportClick={handleExportClick}
          onFetchNextPage={fetchDomainDashboardReportNextPage}
          onProjectClick={handleProjectClick}
          selections={projectsModal.selections}
          subtitle={projectsModal.subtitle}
          title={projectsModal.title}
        />
      )}
    </Card>
  );
}

ApprovalStatusCard.propTypes = {
  dashboardFilterParams: PropTypes.shape({
    division_ids: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isGeneratingExport: PropTypes.bool.isRequired,
  onExportClick: PropTypes.func.isRequired,
};
